import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const BillingContext = createContext(null);

export function BillingProvider({ children }) {
  const { user, accessToken, organisation } = useAuth();

  // DEMO OVERRIDE: Demo orgs always appear fully active
  const isDemo = organisation?.is_demo === true;
  
  // Initialise synchronously from organisation if already available
  // This prevents one-render flash where paymentMethodOnFile is false
  const orgPaymentOnFile = isDemo ? true : organisation?.payment_method_on_file === true;
  const orgHasData = organisation && typeof organisation.payment_method_on_file !== 'undefined';

  // Billing state
  const [billingLoaded, setBillingLoaded] = useState(isDemo ? true : orgHasData);
  const [paymentMethodOnFile, setPaymentMethodOnFile] = useState(orgPaymentOnFile);
  const [subscriptionActive, setSubscriptionActive] = useState(orgPaymentOnFile);
  const [subscriptionStatus, setSubscriptionStatus] = useState(isDemo ? 'active' : null);
  const [includedSeats, setIncludedSeats] = useState(0);
  const [seatsUsed, setSeatsUsed] = useState(0);
  const [billingHistory, setBillingHistory] = useState([]);
  const [cardLast4, setCardLast4] = useState(null);
  const [cardBrand, setCardBrand] = useState(null);
  const [billingModel, setBillingModel] = useState(null);
  const [orgType, setOrgType] = useState(organisation?.organisation_type || null);
  const [stripeCustomerId, setStripeCustomerId] = useState(null);
  
  // Resolve billingLoaded immediately if org data is already in context
  useEffect(() => {
    if (organisation && typeof organisation.payment_method_on_file !== 'undefined') {
      // DEMO OVERRIDE: Demo orgs always show as fully active
      if (organisation.is_demo) {
        setPaymentMethodOnFile(true);
        setSubscriptionActive(true);
        setSubscriptionStatus('active');
        setBillingLoaded(true);
        return;
      }
      setPaymentMethodOnFile(organisation.payment_method_on_file === true);
      setOrgType(organisation.organisation_type);
      setBillingLoaded(true);
    }
  }, [organisation]);
  
  // Fetch billing data once on mount when authenticated
  const fetchBillingData = useCallback(async () => {
    if (!accessToken) {
      setBillingLoaded(false);
      return;
    }
    
    // Check if user is a B2B user
    const role = user?.role || user?.account_type;
    const isB2BUser = role && (
      role.startsWith('b2b_') || 
      ['b2b_owner', 'b2b_admin', 'b2b_member', 'b2b_solicitor', 'b2b_accountant', 'b2b_funeral'].includes(role)
    );
    
    if (!isB2BUser) {
      // Non-B2B users don't need billing state
      setBillingLoaded(true);
      return;
    }
    
    try {
      // Single fetch for all billing data from org endpoint
      const orgRes = await fetch(`${API}/api/b2b/organisation`, {
        headers: { 'Authorization': `Bearer ${accessToken}` }
      });
      
      if (!orgRes.ok) {
        console.error('[BillingContext] Failed to fetch organisation');
        setBillingLoaded(true);
        return;
      }
      
      const orgData = await orgRes.json();
      
      // DEMO OVERRIDE: Demo orgs always show as fully active regardless of DB
      if (orgData.is_demo) {
        setOrgType(orgData.organisation_type);
        setBillingModel(orgData.billing_model || 'per_case');
        setPaymentMethodOnFile(true);
        setSubscriptionActive(true);
        setSubscriptionStatus('active');
        setStripeCustomerId(orgData.stripe_customer_id);
        setIncludedSeats(orgData.included_seats || 1);
        setSeatsUsed(orgData.members_count || 1);
        setCardLast4('0000');
        setCardBrand('demo');
        setBillingLoaded(true);
        return;
      }
      
      // Set all billing state from single response
      setOrgType(orgData.organisation_type);
      setBillingModel(orgData.billing_model || 'per_case');
      setPaymentMethodOnFile(orgData.payment_method_on_file === true);
      setStripeCustomerId(orgData.stripe_customer_id);
      setIncludedSeats(orgData.included_seats || 1);
      setSeatsUsed(orgData.members_count || 1);
      
      // Subscription status for accountancy firms
      if (orgData.organisation_type === 'accountancy_firm') {
        setSubscriptionStatus(orgData.subscription_status);
        setSubscriptionActive(orgData.subscription_status === 'active');
      } else {
        // Law firms don't have subscriptions but can be active with payment method
        setSubscriptionActive(orgData.payment_method_on_file === true);
      }
      
      // Card details from stripe_payment_method_id
      if (orgData.card_last4) {
        setCardLast4(orgData.card_last4);
        setCardBrand(orgData.card_brand);
      }
      
      // Mark billing as loaded - this is the critical flag
      setBillingLoaded(true);
      
    } catch (error) {
      console.error('[BillingContext] Error fetching billing data:', error);
      // Even on error, mark as loaded to prevent infinite loading
      setBillingLoaded(true);
    }
  }, [accessToken, user]);
  
  // Reset billing state on logout
  const resetBillingState = useCallback(() => {
    setBillingLoaded(false);
    setPaymentMethodOnFile(false);
    setSubscriptionActive(false);
    setSubscriptionStatus(null);
    setIncludedSeats(0);
    setSeatsUsed(0);
    setBillingHistory([]);
    setCardLast4(null);
    setCardBrand(null);
    setBillingModel(null);
    setOrgType(null);
    setStripeCustomerId(null);
  }, []);
  
  // Refetch billing data (for use after payment method changes)
  const refetchBilling = useCallback(async () => {
    setBillingLoaded(false);
    await fetchBillingData();
  }, [fetchBillingData]);
  
  // Fetch on mount and when auth changes
  useEffect(() => {
    if (accessToken) {
      fetchBillingData();
    } else {
      resetBillingState();
    }
  }, [accessToken, fetchBillingData, resetBillingState]);
  
  const value = {
    // Loading state - CRITICAL: must be checked before rendering billing UI
    billingLoaded,
    
    // Billing data
    paymentMethodOnFile,
    subscriptionActive,
    subscriptionStatus,
    includedSeats,
    seatsUsed,
    billingHistory,
    cardLast4,
    cardBrand,
    billingModel,
    orgType,
    stripeCustomerId,
    
    // Actions
    refetchBilling,
    resetBillingState,
  };
  
  return (
    <BillingContext.Provider value={value}>
      {children}
    </BillingContext.Provider>
  );
}

export function useBilling() {
  const context = useContext(BillingContext);
  if (!context) {
    // Return safe defaults if used outside provider
    return {
      billingLoaded: false,
      paymentMethodOnFile: false,
      subscriptionActive: false,
      subscriptionStatus: null,
      includedSeats: 0,
      seatsUsed: 0,
      billingHistory: [],
      cardLast4: null,
      cardBrand: null,
      billingModel: null,
      orgType: null,
      stripeCustomerId: null,
      refetchBilling: () => {},
      resetBillingState: () => {},
    };
  }
  return context;
}

// Skeleton component for billing loading state
export function BillingSkeleton() {
  return (
    <div className="animate-pulse space-y-6 p-6">
      {/* Stats skeleton */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {[1, 2, 3, 4].map(i => (
          <div key={i} className="bg-slate-200 dark:bg-slate-700 rounded-xl h-24"></div>
        ))}
      </div>
      
      {/* Content skeleton */}
      <div className="bg-slate-200 dark:bg-slate-700 rounded-xl h-64"></div>
      
      {/* Billing card skeleton */}
      <div className="bg-slate-200 dark:bg-slate-700 rounded-xl h-32"></div>
    </div>
  );
}

export default BillingContext;
